import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FilePdf, Eye, DownloadSimple, Printer, CircleNotch } from "@phosphor-icons/react";
import api, { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { mediaSrc } from "@/components/service/serviceApi";
import { rangeFor } from "@/components/dailylogs/OverviewBoard";

const printPdf = (url) => {
  const f = document.createElement("iframe");
  f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  f.src = url;
  f.onload = () => { try { f.contentWindow.focus(); f.contentWindow.print(); } catch { window.open(url, "_blank"); } setTimeout(() => f.remove(), 60000); };
  document.body.appendChild(f);
};

function ReportRow({ r }) {
  const k = r.kpis || {};
  const period = r.date_from === r.date_to ? r.date_from : `${r.date_from} → ${r.date_to}`;
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-b-0" data-testid={`dl-report-${r.id}`}>
      <FilePdf size={22} className="text-rose-500" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900">{period} <span className="text-xs text-slate-400">v{r.version}</span></div>
        <div className="text-[11px] text-slate-500">{k.crew_count ?? 0} crews · {k.sites ?? 0} sites · {k.completed ?? 0}/{k.assigned ?? 0} reported complete · {k.photos ?? 0} photos · {k.videos ?? 0} videos · {new Date(r.created_at).toLocaleString()} · {r.created_by}</div>
      </div>
      <a href={mediaSrc(r.view_url)} target="_blank" rel="noreferrer" data-testid={`dl-report-view-${r.id}`}><Button size="sm" variant="outline"><Eye size={14} className="mr-1" />View PDF</Button></a>
      <a href={mediaSrc(r.download_url)} data-testid={`dl-report-download-${r.id}`}><Button size="sm" variant="outline"><DownloadSimple size={14} className="mr-1" />Download PDF</Button></a>
      <Button size="sm" onClick={() => printPdf(mediaSrc(r.view_url))} data-testid={`dl-report-print-${r.id}`}><Printer size={14} className="mr-1" />Print</Button>
    </div>
  );
}

export function ReportsTab() {
  const [y] = rangeFor("yesterday");
  const [from, setFrom] = useState(y);
  const [to, setTo] = useState(y);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/dailylogs/reports").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const gen = async () => {
    setBusy(true);
    try { await api.post("/dailylogs/reports", { date_from: from, date_to: to }); toast.success("PDF generated"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not generate PDF")); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="dl-reports">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); if (to < e.target.value) setTo(e.target.value); }} className="h-9 w-40" data-testid="dl-report-from" />
        <span className="text-xs text-slate-400">to</span>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" data-testid="dl-report-to" />
        <Button onClick={gen} disabled={busy || to < from} data-testid="dl-report-generate">{busy ? <CircleNotch size={14} className="mr-1.5 animate-spin" /> : <FilePdf size={14} className="mr-1.5" />}Generate PDF</Button>
        <span className="text-[11px] text-slate-500">Crew-first, US Letter. Reports are also generated automatically by the 3 AM consolidation; regenerate after corrections to create a new version.</span>
      </div>
      <div className="bg-white rounded-xl border border-slate-200">
        {rows.length === 0 ? <div className="text-sm text-slate-500 py-8 text-center" data-testid="dl-reports-empty">No reports yet.</div> : rows.map((r) => <ReportRow key={r.id} r={r} />)}
      </div>
    </div>
  );
}
